import * as React from 'react';
import { Skeleton } from '@mui/material';
import { DefaultTheme, styled, ThemeProvider } from 'styled-components';
import { CardArrowButton, OldCardContainer, OldCardTitle, TextContainer } from '../../CardLayout';
import { bbocWelcomePageUrl } from '../../../../../core/constants';
import {
	aboveCommonElementsZIndex,
	cardBorder,
	boxShadow,
	whiteColour,
	tabletPortraitMinWidth
} from '../../../../cssConstants';
import { greenTheme, greyTheme, redTheme, yellowTheme } from '../../../../themes/bbocTheme';
import overallResultData from '../../../../../core/bbocResultData';
import BbocProgressBar from './BbocProgressBar';
import { useNavigateWithAppName } from '../../../../common/NavigateWithAppName';
import useClientContextStore from '../../../../../zustandStore/clientContextStore';
import CustomTooltip from '../../../../common/CustomTooltip';
import progressScoreTooltip from '../../../bboc/bBocScore';

const BbocScoreCard = ({ overallScore = null, showButton = true, showTooltip = false }: Props) => {
	const navigate = useNavigateWithAppName();
	const clientDetails = useClientContextStore((state) => state.clientDetails);
	const score = clientDetails?.latestBbocScore || overallScore;

	const handleCardClick = () => {
		navigate(bbocWelcomePageUrl);
	};

	if (clientDetails === undefined || overallScore === undefined) {
		return (
			<Container>
				<Flex>
					<Skeleton variant="circular" width={74} height={74} sx={{ zIndex: aboveCommonElementsZIndex }} />
				</Flex>
				<SkeletonCardContainer>
					<SkeletonTextBlock>
						<Skeleton variant="text" width="65%" height={24} />
						<Skeleton variant="text" width="45%" height={20} />
						<SkeletonProgressBar>
							{[0, 1, 2, 3, 4].map((i) => (
								<SkeletonChunk key={i}>
									<SkeletonBar $index={i} />
									<Skeleton variant="text" width="60%" height={10} sx={{ marginTop: '3px' }} />
									<Skeleton variant="text" width="50%" height={10} />
								</SkeletonChunk>
							))}
						</SkeletonProgressBar>
						{showButton && (
							<Skeleton
								variant="rectangular"
								width={24}
								height={24}
								sx={{ borderRadius: '4px', marginTop: '12px', alignSelf: 'flex-end' }}
							/>
						)}
					</SkeletonTextBlock>
				</SkeletonCardContainer>
			</Container>
		);
	}

	return (
		<ThemeProvider theme={getTheme(score) as DefaultTheme}>
			<Container onClick={handleCardClick}>
				<Flex>
					<ScoreCircle>{score ?? 0}</ScoreCircle>
					{showTooltip && (
						<TooltipContainer>
							<CustomTooltip data={progressScoreTooltip} />
						</TooltipContainer>
					)}
				</Flex>
				<StyledCardContainer $showButton={showButton}>
					<TextContainer>
						<OldCardTitle>Financial Progress Score</OldCardTitle>
						<SubTitle>{getBbocResultTitle(score)}</SubTitle>
						<BbocProgressBar score={score} />
						{showButton && <CardArrowButton />}
					</TextContainer>
				</StyledCardContainer>
			</Container>
		</ThemeProvider>
	);
};

export default BbocScoreCard;

interface Props {
	overallScore?: number;
	showButton?: boolean;
	showTooltip?: boolean;
}

const getTheme = (score: number | null) => {
	if (!score) return greyTheme;
	if (score < 50) return redTheme;
	if (score < 85) return yellowTheme;
	return greenTheme;
};

const getBbocResultTitle = (score: number | null) => {
	if (!score) return 'Tap to get a score';

	const result = overallResultData.find((x) => x.min <= score && score <= x.max);
	return result.title;
};

const Container = styled.section`
	margin-top: 37px;
	position: relative;
`;

const ScoreCircle = styled.p`
	background-color: ${(props) => props.theme.progressColour};
	width: 74px;
	height: 74px;
	border: ${cardBorder};
	border-radius: 50%;
	box-shadow: ${boxShadow};

	text-align: center;
	color: ${whiteColour};
	font-size: 32px;
	line-height: 72px;
	font-weight: 600;
	z-index: ${aboveCommonElementsZIndex};
`;

const StyledCardContainer = styled(OldCardContainer)<{ $showButton: boolean }>`
	/* Add padding at the bottom to make room for the button */
	padding: 40px 0 ${({ $showButton }) => ($showButton ? '40px' : '0')} 0;
	text-align: center;
`;

const SubTitle = styled.p`
	color: ${(props) => props.theme.progressColour};
	font-size: 14px;
	line-height: 20px;
	font-weight: 500;
	letter-spacing: 0.1px;

	@media (min-width: ${tabletPortraitMinWidth}) {
		font-size: 18px;
		line-height: 24px;
	}
`;

const Flex = styled.div`
	display: flex;
	justify-content: center;
	position: absolute;
	top: -37px;
	width: 100%;
`;

const TooltipContainer = styled.div`
	position: absolute;
	top: -5px;
	right: 5px;
`;

const SkeletonCardContainer = styled(OldCardContainer)`
	padding: 40px 0 16px 0;
	text-align: center;
`;

const SkeletonTextBlock = styled.div`
	display: flex;
	flex-direction: column;
	align-items: center;
	width: 100%;
	padding: 0 16px;
	box-sizing: border-box;
`;

const SkeletonProgressBar = styled.div`
	display: flex;
	width: 100%;
	margin-top: 16px;
`;

const SkeletonChunk = styled.div`
	display: inline-flex;
	flex-direction: column;
	width: calc((100% - 16px) / 5);
	margin-right: 4px;
	align-items: flex-start;

	&:last-child {
		margin-right: 0;
	}
`;

const getBarRadius = (index: number) => {
	if (index === 0) return '5px 0 0 5px';
	if (index === 4) return '0 5px 5px 0';
	return '0';
};

const SkeletonBar = styled(Skeleton).attrs({ variant: 'rectangular' as const })<{ $index: number }>`
	height: 10px !important;
	width: 100% !important;
	border-radius: ${({ $index }) => getBarRadius($index)} !important;
`;
